<template>
  <div class="user-home">
    <!-- 会员状态卡片 -->
    <el-card class="status-card">
      <template #header>
        <div class="card-header">
          <h2>会员状态</h2>
        </div>
      </template>

      <div class="status-content">
        <!-- 未登录 -->
        <div v-if="!isLogin" class="status-section">
          <el-empty description="请先登录">
            <el-button type="primary" @click="goToLogin">立即登录</el-button>
          </el-empty>
        </div>

        <!-- 已登录但未选择会员类型 -->
        <div v-else-if="!hasMemberType" class="status-section">
          <el-result icon="info" title="欢迎加入" sub-title="请选择您的会员类型以继续">
            <template #extra>
              <el-button type="primary" @click="goToTypeSelect">
                选择会员类型
              </el-button>
            </template>
          </el-result>
        </div>

        <!-- 已选择类型但未支付 -->
        <div v-else-if="!hasPaid" class="status-section">
          <el-result icon="warning" title="待支付" :sub-title="`您已选择${memberTypeText}，请完成支付以激活会员权限`">
            <template #extra>
              <el-button type="primary" @click="goToPayment">
                立即支付
              </el-button>
            </template>
          </el-result>
        </div>

        <!-- 审核中 -->
        <div v-else-if="isPending" class="status-section">
          <el-result icon="info" title="审核中" sub-title="您的会员信息正在审核中，请耐心等待">
            <template #extra>
              <div class="member-details">
                <div class="member-profile">
                  <el-avatar :size="80" :src="memberInfo.photo" shape="square">
                    {{ memberInfo.name?.charAt(0) }}
                  </el-avatar>
                  <div class="member-profile-info">
                    <h3 class="member-name">{{ memberInfo.name || '-' }}</h3>
                    <p class="member-title" v-if="memberInfo.title">{{ memberInfo.title }}</p>
                    <div class="member-tags">
                      <el-tag type="info">{{ memberTypeText }}</el-tag>
                      <el-tag type="info" v-if="isIndividual">{{ memberLevelText }}</el-tag>
                      <el-tag type="warning">审核中</el-tag>
                    </div>
                  </div>
                </div>
                <el-descriptions :column="2" >
                  <el-descriptions-item label="姓名">{{ memberInfo.name || '-' }}</el-descriptions-item>
                  <el-descriptions-item label="职称">{{ memberInfo.title || '-' }}</el-descriptions-item>
                  <el-descriptions-item label="地址">{{ memberInfo.address || '-' }}</el-descriptions-item>
                  <el-descriptions-item label="支付时间">{{ memberInfo.pay_time || '-' }}</el-descriptions-item>
                  <el-descriptions-item label="注册时间">{{ memberInfo.create_time || '-' }}</el-descriptions-item>
                  <el-descriptions-item label="审核状态">
                    <el-tag type="info" size="small">审核中</el-tag>
                  </el-descriptions-item>
                </el-descriptions>
                <div class="member-intro" v-if="memberInfo.intro">
                  <h3>个人简介</h3>
                  <p>{{ memberInfo.intro }}</p>
                </div>
              </div>
            </template>
          </el-result>
        </div>

        <!-- 已驳回 -->
        <div v-else-if="isRejected" class="status-section">
          <el-result icon="error" title="审核未通过" :sub-title="memberInfo.audit_remark || '您的会员申请未通过审核，请修改后重新提交'">
            <template #extra>
              <div class="member-details">
                <div class="member-profile">
                  <el-avatar :size="80" :src="memberInfo.photo" shape="square">
                    {{ memberInfo.name?.charAt(0) }}
                  </el-avatar>
                  <div class="member-profile-info">
                    <h3 class="member-name">{{ memberInfo.name || '-' }}</h3>
                    <p class="member-title" v-if="memberInfo.title">{{ memberInfo.title }}</p>
                    <div class="member-tags">
                      <el-tag type="danger">{{ memberTypeText }}</el-tag>
                      <el-tag type="danger" v-if="isIndividual">{{ memberLevelText }}</el-tag>
                      <el-tag type="danger">已驳回</el-tag>
                    </div>
                  </div>
                </div>
                <el-descriptions :column="2" border>
                  <el-descriptions-item label="姓名">{{ memberInfo.name || '-' }}</el-descriptions-item>
                  <el-descriptions-item label="职称">{{ memberInfo.title || '-' }}</el-descriptions-item>
                  <el-descriptions-item label="地址">{{ memberInfo.address || '-' }}</el-descriptions-item>
                  <el-descriptions-item label="驳回原因">
                    <span style="color: #f56c6c">{{ memberInfo.audit_remark || '-' }}</span>
                  </el-descriptions-item>
                </el-descriptions>
                <el-button type="primary" style="margin-top: 20px" @click="goToMemberInfo">
                  修改信息
                </el-button>
              </div>
            </template>
          </el-result>
        </div>

        <!-- 审核通过 -->
        <div v-else-if="isApproved" class="status-section approved">
          <el-result icon="success" title="恭喜您" sub-title="您已成为本协会正式会员">
            <template #extra>
              <div class="member-details">
                <!-- 会员头像与基本信息 -->
                <div class="member-profile">
                  <el-avatar :size="100" :src="memberInfo.photo" shape="square">
                    {{ memberInfo.name?.charAt(0) }}
                  </el-avatar>
                  <div class="member-profile-info">
                    <h3 class="member-name">{{ memberInfo.name || '-' }}</h3>
                    <p class="member-title" v-if="memberInfo.title">{{ memberInfo.title }}</p>
                    <div class="member-tags">
                      <el-tag type="success">{{ memberTypeText }}</el-tag>
                      <el-tag type="success" v-if="isIndividual">{{ memberLevelText }}</el-tag>
                      <el-tag type="warning">已认证</el-tag>
                    </div>
                  </div>
                </div>

                <!-- 详细信息 -->
                <el-descriptions :column="2" border class="member-desc">
                  <el-descriptions-item label="姓名">
                    {{ memberInfo.name || '-' }}
                  </el-descriptions-item>
                  <el-descriptions-item label="职称">
                    {{ memberInfo.title || '-' }}
                  </el-descriptions-item>
                  <el-descriptions-item label="会员类型">
                    <el-tag type="success" size="small">{{ memberTypeText }}</el-tag>
                  </el-descriptions-item>
                  <el-descriptions-item label="会员等级" v-if="isIndividual">
                    <el-tag type="success" size="small">{{ memberLevelText }}</el-tag>
                  </el-descriptions-item>
                  <el-descriptions-item label="支付状态">
                    <el-tag :type="memberInfo.pay_status === 1 ? 'success' : 'danger'" size="small">
                      {{ memberInfo.pay_status === 1 ? '已支付' : '未支付' }}
                    </el-tag>
                  </el-descriptions-item>
                  <el-descriptions-item label="支付时间">
                    {{ memberInfo.pay_time || '-' }}
                  </el-descriptions-item>
                  <el-descriptions-item label="地址">
                    {{ memberInfo.address || '-' }}
                  </el-descriptions-item>
                  <el-descriptions-item label="审核状态">
                    <el-tag type="success" size="small">已通过</el-tag>
                  </el-descriptions-item>
                  <el-descriptions-item label="注册时间">
                    {{ memberInfo.create_time || '-' }}
                  </el-descriptions-item>
                  <el-descriptions-item label="更新时间">
                    {{ memberInfo.update_time || '-' }}
                  </el-descriptions-item>
                </el-descriptions>

                <!-- 简介 -->
                <div class="member-intro" v-if="memberInfo.intro">
                  <h3>个人简介</h3>
                  <p>{{ memberInfo.intro }}</p>
                </div>

                <!-- 详细介绍 -->
                <div class="member-content" v-if="memberInfo.content">
                  <h3>详细介绍</h3>
                  <div class="content-text">{{ memberInfo.content }}</div>
                </div>

                <!-- 会员权益 -->
                <div class="permissions-section">
                  <h3>您的会员权益</h3>
                  <el-row :gutter="20">
                    <el-col :span="8" v-for="(perm, index) in permissions" :key="index">
                      <div class="permission-item">
                        <el-icon :size="24" color="#67c23a">
                          <Check />
                        </el-icon>
                        <span>{{ perm }}</span>
                      </div>
                    </el-col>
                  </el-row>
                </div>

                <!-- 快捷操作 -->
                <div class="quick-actions">
                  <h3>快捷操作</h3>
                  <el-row :gutter="20">
                    <el-col :span="6">
                      <el-button type="primary" @click="goToCourses">
                        浏览课程
                      </el-button>
                    </el-col>
                    <el-col :span="6" v-if="isDirector">
                      <el-button type="success" @click="goToPublishNews">
                        发布新闻
                      </el-button>
                    </el-col>
                    <el-col :span="6">
                      <el-button @click="goToMemberInfo">
                        修改信息
                      </el-button>
                    </el-col>
                  </el-row>
                </div>
              </div>
            </template>
          </el-result>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import { Check } from '@element-plus/icons-vue'
import {
  MEMBER_LEVEL,
  MEMBER_TYPE_NAMES,
  MEMBER_LEVEL_NAMES
} from '@/utils/constants'
import categoryApi from '@/api/index'

export default {
  components: { Check },
  computed: {
    isLogin() { return this.$store.getters['auth/isLogin'] },
    memberInfo() { return this.$store.getters['auth/memberInfo'] },
    memberType() { return this.$store.getters['auth/memberType'] },
    memberLevel() { return this.$store.getters['auth/memberLevel'] },
    hasPaid() { return this.$store.getters['auth/hasPaid'] },
    isIndividual() { return this.$store.getters['auth/isIndividual'] },
    isOrganization() { return this.$store.getters['auth/isOrganization'] },
    isDirector() { return this.$store.getters['auth/isDirector'] },
    isPending() { return this.$store.getters['auth/isPending'] },
    isApproved() { return this.$store.getters['auth/isApproved'] },
    isRejected() { return this.$store.getters['auth/isRejected'] },
    hasMemberType() { return this.memberType !== null && this.memberType !== undefined },
    memberTypeText() { return MEMBER_TYPE_NAMES[this.memberType] || '-' },
    memberLevelText() { return MEMBER_LEVEL_NAMES[this.memberLevel] || '-' },
    permissions() {
      const perms = []
      if (this.isIndividual) {
        perms.push('浏览课程信息', '购买课程')
        if (this.memberLevel === MEMBER_LEVEL.REGISTERED) {
          perms.push('个人信息展示', '优先参与活动')
        }
      } else if (this.isOrganization) {
        perms.push('机构信息展示', '社会服务搜索', '查看完整会员信息')
      } else if (this.isDirector) {
        perms.push('浏览课程信息', '购买课程', '发布新闻', '管理新闻', '参与协会决策')
      }
      return perms
    }
  },
  async mounted() {
    if (this.isLogin) {
      try {
        const res = await categoryApi.info()
        if (res && res.code === 2000) {
          this.$store.dispatch('auth/refreshMemberInfo', res.data)
        }
      } catch (e) {
        console.error('获取会员信息失败', e)
      }
    }
  },
  methods: {
    goToLogin() { this.$router.push('/login') },
    goToTypeSelect() { this.$router.push('/user/member-type-select') },
    goToPayment() {
      this.$router.push({
        path: '/user/member-payment',
        query: { type: this.memberType, level: this.memberLevel }
      })
    },
    goToMemberInfo() { this.$router.push('/user/member-info') },
    goToCourses() { this.$router.push('/course/list') },
    goToPublishNews() { this.$router.push('/news/publish') }
  }
}
</script>

<style lang="scss" scoped>
.user-home {
  min-height: 100vh;
  padding: 40px 20px;
  background-color: #f5f7fa;

  .status-card {
    max-width: 1400px;
    margin: 0 auto;

    .card-header {
      text-align: center;

      h2 {
        margin: 0;
        font-size: 24px;
        color: #303133;
      }
    }

    .status-content {
      min-height: 400px;

      .status-section {
        padding: 40px 20px;

        .member-details {
          width: 100%;
          margin-top: 20px;

          .member-profile {
            display: flex;
            align-items: center;
            gap: 24px;
            padding: 24px;
            background: linear-gradient(135deg, #f0f9ff 0%, #e8f5e9 100%);
            border-radius: 8px;
            margin-bottom: 24px;

            .member-profile-info {
              .member-name {
                margin: 0 0 6px 0;
                font-size: 22px;
                color: #303133;
              }

              .member-title {
                margin: 0 0 10px 0;
                font-size: 14px;
                color: #909399;
              }

              .member-tags {
                display: flex;
                gap: 8px;
              }
            }
          }

          .member-desc {
            margin-bottom: 24px;
          }

          .member-intro,
          .member-content {
            margin-top: 20px;
            padding-top: 16px;
            border-top: 1px solid #e4e7ed;

            h3 {
              margin: 0 0 12px 0;
              font-size: 16px;
              color: #303133;
            }

            p, .content-text {
              color: #606266;
              font-size: 14px;
              line-height: 1.8;
            }
          }

          .permissions-section,
          .quick-actions {
            margin-top: 24px;
            padding-top: 20px;
            border-top: 1px solid #e4e7ed;

            h3 {
              margin: 0 0 20px 0;
              font-size: 16px;
              color: #303133;
            }

            .permission-item {
              display: flex;
              align-items: center;
              gap: 10px;
              padding: 10px;
              background-color: #f0f9ff;
              border-radius: 4px;
              margin-bottom: 10px;

              span {
                color: #606266;
              }
            }
          }

          .quick-actions {
            .el-button {
              width: 100%;
            }
          }
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .user-home {
    padding: 20px 10px;

    .status-card {
      .status-section {
        padding: 20px 10px;

        &.approved {
          .member-details {
            .permissions-section {
              .el-col {
                margin-bottom: 10px;
              }
            }

            .quick-actions {
              .el-col {
                margin-bottom: 10px;
              }
            }
          }
        }
      }
    }
  }
}
</style>
